
import React, { useState, useMemo, useEffect } from 'react';

const ImpdsDetails = ({ data }) => {

  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString;
    const [year, mon, day] = parts;
    return `${day.padStart(2,'0')}-${mon.padStart(2,'0')}-${year}`;
  };

  const decimalThree = (value) => parseFloat(value || 0).toFixed(3);

  const msg = data.status === 'I' ? 'IN' : 'OUT';
  const monthInt = data.month ? parseInt(data.month) : 0;

  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setCurrentPage(1);
  }, [perPage, search, data.details]);

  //  Dynamic Commodity Headers
  const commodityHeaders = useMemo(() => {
    if (!data.details || data.details.length === 0) return [];
    return data.details[0].commodityList || [];
  }, [data.details]);

  // Dynamic Totals
  const commodityTotals = useMemo(() => {
    const totals = {};
    if (!data.details) return totals;

    data.details.forEach(detail => {
      detail.commodityList?.forEach(comm => {
        if (!totals[comm.commId]) {
          totals[comm.commId] = 0;
        }
        totals[comm.commId] += parseFloat(comm.saleQty) || 0;
      });
    });

    return totals;
  }, [data.details]);

  const filteredData = useMemo(() => {
    if (!data.details) return [];
    const query = search.toLowerCase();

    return data.details.filter(d =>
      Object.values(d).some(val =>
        val !== null &&
        val !== undefined &&
        val.toString().toLowerCase().includes(query)
      )
    );
  }, [data.details, search]);

  const totalEntries = filteredData.length;
  const totalPages = perPage === "ALL" ? 1 : Math.max(1, Math.ceil(totalEntries / perPage));

  const paginatedData = useMemo(() => {
    if (perPage === "ALL") return filteredData;
    const start = (currentPage - 1) * perPage;
    return filteredData.slice(start, start + perPage);
  }, [filteredData, perPage, currentPage]);

  const startEntry = totalEntries === 0 ? 0 :
    (currentPage - 1) * (perPage === "ALL" ? totalEntries : perPage) + 1;

  const endEntry = perPage === "ALL"
    ? totalEntries
    : Math.min(currentPage * perPage, totalEntries);

  return (
    <div style={{ textAlign: 'center', margin: '10px auto', maxWidth: '1300px' }}>

      {data.details && data.details.length > 0 ? (
        <>
          {/*  Search + Pagination Controls */}
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '10px 0' }}>
            <div>
              <label>Per page: </label>
              <select
                value={perPage}
                onChange={(e) => {
                  const v = e.target.value === "ALL" ? "ALL" : parseInt(e.target.value, 10);
                  setPerPage(v);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="ALL">ALL</option>
              </select>
            </div>

            <div>
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ padding: "4px", minWidth: "200px" }}
              />
            </div>
          </div>

          {/* 🔹 Table */}
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                minWidth: '1200px',
                borderCollapse: "collapse",
                width: "100%"
              }}
            >
              <thead>
                <tr className='tableheader'>
                  <th colSpan={10 + commodityHeaders.length}>
                    {data.transName} {msg} Transactions Details for{" "}
                    <span style={{ color: 'orange' }}>
                      {monthNames[monthInt - 1]}
                    </span> '
                    <span style={{ color: 'orange' }}>{data.year}</span>
                  </th>
                </tr>

                <tr className='tableheader'>
                  <th rowSpan="2" >Sl No</th>
                  <th rowSpan="2" >RC No</th>
                  <th rowSpan="2" >Name</th>
                  <th rowSpan="2" >Scheme</th>
                  <th rowSpan="2" >Availed Date</th>
                  <th rowSpan="2" >Receipt No</th>

                  <th colSpan="2" >Home State</th>
                  <th colSpan="2" >Sale State</th>

                  <th colSpan={commodityHeaders.length}
                      >
                    Availed Qty (in Kgs)
                  </th>
                </tr>

                <tr className='tableheader'>
                  <th >State</th>
                  <th >FPS</th>
                  <th >District</th>
                  <th >FPS</th>

                  {commodityHeaders.map((comm, idx) => (
                    <th key={idx} >
                      {comm.commName}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {paginatedData.map((detail, i) => (
                  <tr key={i}>
                    <td style={bodyStyle()}>
                      {(currentPage - 1) * (perPage === "ALL" ? totalEntries : perPage) + i + 1}
                    </td>
                    <td style={bodyStyle()}>{detail.rcId}</td>
                    <td style={bodyStyle()}>{detail.memberName}</td>
                    <td style={bodyStyle()}>{detail.schemeName}</td>
                    <td style={bodyStyle()}>{formatDate(detail.transDate)}</td>
                    <td style={bodyStyle()}>{detail.receiptId}</td>
                    <td style={bodyStyle()}>{detail.homeState}</td>
                    <td style={bodyStyle()}>{detail.homeFpsId}</td>
                    <td style={bodyStyle()}>{detail.distNameEn}</td>
                    <td style={bodyStyle()}>{detail.saleFpsId}</td>

                    {detail.commodityList?.map((comm, idx) => (
                      <td key={idx} style={bodyStyle()} align="right">
                        {decimalThree(comm.saleQty)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr className='tableheader'>
                  <td colSpan="10">
                    Total
                  </td>

                  {commodityHeaders.map((comm, idx) => (
                    <td key={idx} >
                      {decimalThree(commodityTotals[comm.commId])}
                    </td>
                  ))}
                </tr>
              </tfoot>

            </table>
          </div>

          {/*  Footer */}
          <div style={{ marginTop: "10px" }}>
            Showing {startEntry} to {endEntry} of {totalEntries} entries
          </div>
        </>
      ) : (
        <div>No Transaction Available</div>
      )}
    </div>
  );
};



const bodyStyle = () => ({
  padding: '6px',
  border: '1px solid #ddd'
});



export default ImpdsDetails;